import { useEffect, useRef } from "react";

type EventTargetLike = Window | Document | HTMLElement | null | undefined;

type UseEventListenerOptions = {
  /** リスナーを登録する対象。省略時は window。関数を渡すと登録時に評価される。 */
  target?: EventTargetLike | (() => EventTargetLike);
  /** addEventListener の第3引数。 */
  listenerOptions?: boolean | AddEventListenerOptions;
  /** false の間はリスナーを登録しない。 */
  enabled?: boolean;
};

/**
 * addEventListener / removeEventListener を宣言的に扱う。
 * handler は ref 経由で常に最新版が呼ばれるため、再登録は発生しない。
 */
export const useEventListener = <K extends keyof WindowEventMap>(
  type: K,
  handler: (event: WindowEventMap[K]) => void,
  options?: UseEventListenerOptions
): void => {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  const { target, listenerOptions, enabled = true } = options ?? {};

  useEffect(() => {
    if (!enabled) return;
    const element: EventTargetLike = typeof target === "function" ? target() : (target ?? window);
    if (!element) return;

    const listener = (event: Event) => handlerRef.current(event as WindowEventMap[K]);
    element.addEventListener(type, listener, listenerOptions);
    return () => element.removeEventListener(type, listener, listenerOptions);
  }, [type, target, enabled, listenerOptions]);
};
